import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { RateText, SummaryFigures } from '../../salariesMeta';

type RegisterSummaryProps = {
    /** `null` while the register is in flight — labels stay, values skeleton. */
    figures: SummaryFigures | null;
    /** Today's rates; `null` when no rate set is in force. */
    rates: RateText | null;
};

const Figure: React.FC<{
    label: string;
    /** `null` renders a skeleton in the value's place. */
    value: string | null;
    hint?: string;
    slot: string;
}> = ({ label, value, hint, slot }) => (
    <div className="flex flex-col gap-1 rounded-xl bg-surface-secondary p-4">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </span>
        {value === null ? (
            <Skeleton className="my-1 h-7 w-28 rounded" />
        ) : (
            <span
                className="text-2xl font-semibold tabular-nums text-foreground"
                {...getNodeTestIdProp(`salaries-summary-${slot}`)}
            >
                {value}
            </span>
        )}
        {hint ? <span className="text-xs text-muted">{hint}</span> : null}
    </div>
);

/**
 * The register's three headline figures.
 *
 * **Employer cost is gross + the employer's 2%, and nothing else.** The income
 * tax and the employee's pension are withheld *out of* gross — they are already
 * inside the first figure, and adding them here would overstate what payroll
 * costs the company by 22%. The hint on the third card says so, because a number
 * labelled only "employer cost" invites exactly that mistake.
 *
 * While the register loads the three labels stay put and only the values are
 * skeletoned (the repo's loading-state standard), so the cards never reflow and
 * the user can already read what each figure will say. The employer-cost hint
 * interpolates an amount **and the rate that produced it**, so it is withheld
 * until both are known — a rate quoted beside a figure it did not produce is
 * worse than no rate at all, and these figures use the set in force *today*.
 */
export const RegisterSummary: React.FC<RegisterSummaryProps> = ({
    figures,
    rates,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="grid gap-4 sm:grid-cols-3">
            <Figure
                label={t('salaries.summary.activeEmployees')}
                slot="count"
                value={figures ? String(figures.activeEmployeeCount) : null}
            />
            <Figure
                hint={t('salaries.summary.grossHint')}
                label={t('salaries.summary.monthlyGross')}
                slot="gross"
                value={figures?.monthlyGrossText ?? null}
            />
            <Figure
                {...(figures
                    ? {
                          hint: rates
                              ? t('salaries.summary.employerCostHint', {
                                    amount: figures.employerPensionText,
                                    rate: rates.employerPension,
                                })
                              : t('salaries.summary.employerCostHintNoRate', {
                                    amount: figures.employerPensionText,
                                }),
                      }
                    : {})}
                label={t('salaries.summary.employerCost')}
                slot="employer-cost"
                value={figures?.employerCostText ?? null}
            />
        </div>
    );
};
